import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./redux/auth/authActions";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase/firebase";

import Home from "./pages/Home";
import SubmitApplication from "./components/SubmitApplication";
import AdminPage from "./pages/AdminPage";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./routes/AdminRoute";
import Navbar from "./components/Navbar";
import MyApplicationPage from "./pages/MyApplicationPage";
import SuccessPage from "./pages/SuccessPage";
import TestimonialForm from "./components/TestimonialForm";
import AdminTestimonials from "./components/AdminTestimonials";
import TestimonialsPage from "./pages/TestimonialsPage";
import LandingPage from "./pages/LandingPage";
import CoverLetterModal from "./pages/CoverLetterModal";

import { ADMIN_EMAIL } from "./config/adminConfig";
import ResumeScoreModal from "./pages/ResumeScoreModal";

const AppWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.currentUser);

  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // 🔐 Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        const isAdminUser = currentUser.email === ADMIN_EMAIL;

        dispatch(setUser(currentUser));
        setIsAdmin(isAdminUser);

        // ✅ If coming from root, redirect accordingly
        if (location.pathname === "/") {
          if (isAdminUser) {
            navigate("/admin");
          } else {
            navigate("/home");
          }
        }
      } else {
        setIsAdmin(false);
        dispatch(setUser(null));
      }

      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [location.pathname, navigate, dispatch]);

  // ✅ FIX: Allow admin to access allowed admin routes without force redirect
  useEffect(() => {
    const allowedAdminRoutes = ["/admin", "/admin-testimonials"];
    if (user && isAdmin && !allowedAdminRoutes.includes(location.pathname)) {
      navigate("/admin");
    }
  }, [user, isAdmin, location.pathname, navigate]);

  if (!authChecked) {
    return <div>Authenticating user...</div>;
  }

  const showNavbar = user;

  return (
    <>
      {showNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<Home />} />
        <Route
          path="/submit-application"
          element={
            <ProtectedRoute user={user} isAdmin={isAdmin}>
              <SubmitApplication user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <AdminPage user={user} />
            </AdminRoute>
          }
        />
        <Route path="/success-page" element={<SuccessPage user={user} />} />
        <Route path="/cover-letter" element={<CoverLetterModal />} />
        <Route path="/resume-score" element={<ResumeScoreModal />} />

        <Route path="/share-feedback" element={<TestimonialForm />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route
          path="/admin-testimonials"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <AdminTestimonials />
            </AdminRoute>
          }
        />
        <Route
          path="/my-application"
          element={
            <ProtectedRoute user={user} isAdmin={isAdmin}>
              <MyApplicationPage user={user} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

const App = () => (
  <Router>
    <AppWrapper />
  </Router>
);

export default App;
